import styled from 'styled-components';

// Container principal
export const HeroContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #0a192f 0%, #051937 50%, #000c24 100%);
  color: white;
  position: relative;
  overflow: hidden;
  padding-top: 80px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, 
      rgba(64, 128, 255, 0) 0%,
      rgba(64, 128, 255, 0.8) 50%,
      rgba(64, 128, 255, 0) 100%);
    animation: scanline 3s linear infinite;
    z-index: 10;
    opacity: 0.7;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    pointer-events: none !important;
  }
  
  @keyframes scanline {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(100vh);
    }
  }
  
  /* Media queries para responsividade */
  @media (max-width: 768px) {
    padding-top: 60px;
  }
  
  @media (max-width: 480px) {
    padding-top: 40px;
  }
`;

// Fundo com efeito de ondas e elementos tecnológicos
export const WavyBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(ellipse at 30% 40%, rgba(64, 128, 255, 0.2) 0%, rgba(64, 128, 255, 0) 70%),
    radial-gradient(ellipse at 70% 60%, rgba(32, 156, 255, 0.2) 0%, rgba(32, 156, 255, 0) 70%),
    radial-gradient(ellipse at 40% 80%, rgba(0, 255, 255, 0.15) 0%, rgba(0, 255, 255, 0) 70%);
  z-index: 1;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: 
      repeating-linear-gradient(
        45deg, 
        rgba(64, 128, 255, 0.05) 0px, 
        rgba(64, 128, 255, 0.05) 1px, 
        transparent 1px, 
        transparent 10px
      );
    opacity: 0.3;
    animation: gridMove 40s linear infinite;
  }
  
  &::after {
    transform: rotate(30deg);
    background-image: 
      repeating-linear-gradient(
        -45deg, 
        rgba(0, 255, 255, 0.05) 0px, 
        rgba(0, 255, 255, 0.05) 1px, 
        transparent 1px, 
        transparent 15px
      );
    animation: gridMove 50s linear infinite reverse;
  }
  
  /* Círculos de tecnologia */
  &::before {
    content: '';
    position: absolute;
    width: 40vw;
    height: 40vw;
    border-radius: 50%;
    border: 1px solid rgba(64, 128, 255, 0.1);
    top: 10%;
    left: 30%;
    animation: rotate 60s linear infinite;
  }
  
  @keyframes gridMove {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 100px 100px;
    }
  }
  
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Container de conteúdo
export const ContentContainer = styled.div`
  max-width: 800px;
  text-align: center;
  z-index: 2;
  padding: 0 20px;
  position: relative;
  animation: fadeIn 1s ease-out;
  
  /* Efeito de borda tecnológica */
  &::before, &::after {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    border: 2px solid rgba(64, 128, 255, 0.2);
    border-radius: 20px;
    z-index: -1;
  }
  
  &::before {
    top: -40px;
    left: -40px;
    border-right: none;
    border-bottom: none;
    animation: borderPulse 4s ease-in-out infinite;
  }
  
  &::after {
    bottom: -40px;
    right: -40px;
    border-left: none;
    border-top: none;
    animation: borderPulse 4s ease-in-out infinite 2s;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes borderPulse {
    0% { opacity: 0.2; }
    50% { opacity: 0.8; }
    100% { opacity: 0.2; }
  }
  
  /* Media queries para responsividade */
  @media (max-width: 768px) {
    padding: 0 15px;
    
    &::before, &::after {
      width: 100px;
      height: 100px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0 10px;
    
    &::before, &::after {
      width: 70px;
      height: 70px;
    }
  }
`;

// Título
export const Title = styled.h1`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  .logo-container {
    position: relative;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: logoContainerPulse 6s ease-in-out infinite;
  }
  
  .title-logo {
    width: 80px;
    height: 80px;
    object-fit: contain;
    filter: drop-shadow(0 0 15px rgba(64, 128, 255, 0.8));
    animation: logoFloat 4s ease-in-out infinite, logoPulse 3s ease-in-out infinite alternate;
    position: relative;
    z-index: 5;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      transform: translate(-50%, -50%);
      background: radial-gradient(circle, rgba(0, 255, 255, 0.3) 0%, rgba(0, 255, 255, 0) 70%);
      border-radius: 50%;
      z-index: -1;
      animation: logoGlow 3s ease-in-out infinite;
    }
  }
  
  .logo-tech-circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 110px;
    height: 110px;
    border-radius: 50%;
    border: 2px solid rgba(64, 128, 255, 0.4);
    z-index: 2;
    animation: techCirclePulse 4s ease-in-out infinite alternate;
    
    /* Efeito de círculos girando */
    &::before, &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      border: 1px dashed rgba(0, 255, 255, 0.3);
      animation: circleRotate 10s linear infinite;
    }
    
    &::before {
      width: 140%;
      height: 140%;
      border-style: dashed;
      animation-direction: reverse;
      animation-duration: 15s;
    }
    
    &::after {
      width: 170%;
      height: 170%;
      border-style: dotted;
      opacity: 0.5;
    }
  }
  
  .logo-tech-data {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
    
    /* Linhas de dados digitais */
    &::before, &::after {
      content: '';
      position: absolute;
      background: rgba(0, 255, 255, 0.15);
    }
    
    /* Linhas horizontais */
    &::before {
      top: 50%;
      left: -20px;
      width: 160px;
      height: 1px;
      transform: translateY(-50%);
      box-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
      animation: dataLinePulse 2s infinite alternate;
    }
    
    /* Linhas verticais */
    &::after {
      left: 50%;
      top: -20px;
      width: 1px;
      height: 160px;
      transform: translateX(-50%);
      box-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
      animation: dataLinePulse 2s infinite alternate 1s;
    }
  }
  
  .title-text-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    transform: translate(10px, -10px);
  }
  
  .tech-data-top,
  .tech-data-bottom {
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    color: rgba(0, 255, 255, 0.7);
    letter-spacing: 1px;
    text-shadow: 0 0 5px rgba(0, 255, 255, 0.5);
    position: relative;
    animation: dataBlink 4s infinite alternate;
  }
  
  .tech-data-top {
    align-self: flex-start;
    margin-bottom: 0.3rem;
    opacity: 0.8;
    transform: translate(5px, 10px);
  }
  
  .tech-data-bottom {
    align-self: flex-end;
    margin-top: 0.3rem;
    opacity: 0.8;
  }
  
  .title-text {
    font-size: 5.5rem;
    font-weight: 600;
    letter-spacing: 10px;
    text-shadow: 0 0 15px rgba(64, 128, 255, 0.6), 0 0 40px rgba(64, 128, 255, 0.4);
    position: relative;
    animation: titleGlow 3s ease-in-out infinite alternate;
    font-family: 'Orbitron', 'Rajdhani', sans-serif;
    
    /* Gradiente mais tech */
    background: linear-gradient(to right, 
      rgba(140, 202, 255, 0.95), 
      rgba(92, 227, 255, 0.95), 
      rgba(70, 255, 255, 0.95),
      rgba(92, 227, 255, 0.95)
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: titleGradient 8s linear infinite, titleGlow 3s ease-in-out infinite alternate;
    
    /* Efeito de circuito digital */
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -15px;
      width: calc(100% + 30px);
      height: calc(100% + 20px);
      background: 
        linear-gradient(90deg, transparent 49.5%, rgba(64, 128, 255, 0.15) 50%, transparent 50.5%) 0 0 / 20px 100%,
        linear-gradient(0deg, transparent 49.5%, rgba(64, 128, 255, 0.15) 50%, transparent 50.5%) 0 0 / 100% 20px;
      z-index: -1;
      opacity: 0.7;
      animation: circuitPulse 4s ease-in-out infinite alternate;
    }
  }
  
  /* Media queries para responsividade */
  @media (max-width: 1024px) {
    .title-text {
      font-size: 4.5rem;
      /* Volta para o gradiente original em telas menores */
      background: linear-gradient(to right, 
        rgba(100, 182, 255, 0.9), 
        rgba(52, 207, 235, 0.9), 
        rgba(0, 255, 255, 0.9),
        rgba(52, 207, 235, 0.9)
      );
      background-size: 300% 100%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
    
    .logo-container {
      width: 100px;
      height: 100px;
    }
    
    .title-logo {
      width: 70px;
      height: 70px;
    }
    
    .logo-tech-circle {
      width: 90px;
      height: 90px;
    }
    
    .logo-tech-data {
      &::before {
        width: 130px;
      }
      
      &::after {
        height: 130px;
      }
    }
    
    .title-text {
      font-size: 3.8rem;
      letter-spacing: 8px;
    }
    
    .tech-data-top,
    .tech-data-bottom {
      font-size: 0.8rem;
    }
  }
  
  @media (max-width: 480px) {
    .logo-container {
      width: 80px;
      height: 80px;
      transform: translateY(-20px) !important; /* Subindo a logo em 20px para mobile */
    }
    
    .title-logo {
      width: 55px;
      height: 55px;
    }
    
    .logo-tech-circle {
      width: 75px;
      height: 75px;
    }
    
    .logo-tech-data {
      &::before {
        width: 100px;
      }
      
      &::after {
        height: 100px;
      }
    }
    
    .title-text {
      font-size: 2.8rem;
      letter-spacing: 6px;
    }
    
    .tech-data-top,
    .tech-data-bottom {
      font-size: 0.7rem;
    }
  }
  
  @media (max-width: 360px) {
    .title-text {
      font-size: 2.3rem;
      letter-spacing: 4px;
    }
  }
  
  /* Efeito de glitch sutil */
  .title-text::after {
    content: 'BIANCA';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to right, #64B6FF, #34cfeb, #00ffff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    opacity: 0.4;
    animation: glitch 3s infinite linear alternate-reverse;
    clip-path: polygon(0 80%, 100% 20%, 100% 100%, 0 100%);
    transform: translate(2px, 0);
    opacity: 0.3;
  }
  
  /* Efeito de scanner tecnológico */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent 0%,
      rgba(64, 128, 255, 0.1) 50%,
      transparent 100%
    );
    animation: scanEffect 4s ease-in-out infinite;
    z-index: 1;
    pointer-events: none;
  }
  
  @keyframes logoFloat {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
  
  @keyframes logoPulse {
    0% { filter: drop-shadow(0 0 15px rgba(64, 128, 255, 0.8)); }
    100% { filter: drop-shadow(0 0 25px rgba(0, 255, 255, 0.9)); }
  }
  
  @keyframes circleRotate {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
  
  @keyframes dataLinePulse {
    0% { opacity: 0.3; }
    50% { opacity: 0.8; }
    100% { opacity: 0.3; }
  }
  
  @keyframes dataBlink {
    0%, 100% { opacity: 0.7; }
    30% { opacity: 0.9; }
    33% { opacity: 0.3; }
    36% { opacity: 0.9; }
    85% { opacity: 0.7; }
    88% { opacity: 0.4; }
    90% { opacity: 0.7; }
  }
  
  @keyframes scanEffect {
    0% { left: -100%; }
    50% { left: 100%; }
    100% { left: 100%; }
  }
  
  @keyframes titleGlow {
    0% {
      text-shadow: 0 0 15px rgba(64, 128, 255, 0.6), 0 0 40px rgba(64, 128, 255, 0.4);
    }
    100% {
      text-shadow: 0 0 20px rgba(64, 128, 255, 0.8), 0 0 60px rgba(64, 128, 255, 0.6), 0 0 100px rgba(64, 128, 255, 0.3);
    }
  }
  
  @keyframes glitch {
    0% {
      transform: translate(-2px, 0);
    }
    20% {
      transform: translate(2px, 2px);
    }
    40% {
      transform: translate(-2px, -2px);
    }
    60% {
      transform: translate(2px, 0);
    }
    80% {
      transform: translate(-2px, 2px);
    }
    100% {
      transform: translate(2px, -2px);
    }
  }
  
  @keyframes logoGlow {
    0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.3); }
  }
  
  @keyframes logoContainerPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  @keyframes techCirclePulse {
    0% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 10px rgba(0, 255, 255, 0.2); }
    100% { transform: translate(-50%, -50%) scale(1.1); box-shadow: 0 0 20px rgba(0, 255, 255, 0.4); }
  }
  
  @keyframes titleGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// Subtítulo
export const Subtitle = styled.p`
  font-size: 1.4rem;
  font-family: 'Orbitron', sans-serif;
  line-height: 1.7;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  text-shadow: 0 0 12px rgba(0, 255, 255, 0.5);
  letter-spacing: 2px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.9), #64B6FF, #00FFFF);
  background-size: 300% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: subtitleGradient 8s linear infinite, fadeInUp 1s ease-out 0.3s both;
  
  /* Efeito de destaque nas bordas */
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 80px;
    background: linear-gradient(to right, rgba(64, 128, 255, 0), rgba(64, 196, 255, 0.8), rgba(64, 128, 255, 0));
    box-shadow: 0 0 15px rgba(64, 196, 255, 0.6);
  }
  
  &::before {
    left: 50%;
    top: -15px;
    transform: translateX(-50%);
    animation: widthPulse 4s ease-in-out infinite, glowPulse 3s ease-in-out infinite;
  }
  
  &::after {
    left: 50%;
    bottom: -15px;
    transform: translateX(-50%);
    animation: widthPulse 4s ease-in-out 2s infinite, glowPulse 3s ease-in-out 1.5s infinite;
  }
  
  /* Responsividade */
  @media (max-width: 768px) {
    font-size: 1.2rem;
    letter-spacing: 1.5px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    letter-spacing: 1px;
    padding: 0 15px;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes widthPulse {
    0% { width: 80px; opacity: 0.6; }
    50% { width: 150px; opacity: 1; }
    100% { width: 80px; opacity: 0.6; }
  }
  
  @keyframes subtitleGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes glowPulse {
    0% { box-shadow: 0 0 5px rgba(64, 196, 255, 0.4); }
    50% { box-shadow: 0 0 20px rgba(64, 196, 255, 0.8); }
    100% { box-shadow: 0 0 5px rgba(64, 196, 255, 0.4); }
  }
`;

// Container do botão de telefone
export const PhoneButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  animation: fadeIn 1s ease-out 0.6s both;
  
  /* Efeito de borda tecnológica */
  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(to right, rgba(64, 128, 255, 0), rgba(64, 128, 255, 0.7), rgba(64, 128, 255, 0));
    filter: drop-shadow(0 0 5px rgba(64, 128, 255, 0.5));
    animation: pulseBorder 3s infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(to right, rgba(64, 128, 255, 0), rgba(64, 128, 255, 0.7), rgba(64, 128, 255, 0));
    filter: drop-shadow(0 0 5px rgba(64, 128, 255, 0.5));
    animation: pulseBorder 3s infinite 1.5s;
  }
  
  @keyframes pulseBorder {
    0% { opacity: 0.5; width: 150px; }
    50% { opacity: 1; width: 200px; }
    100% { opacity: 0.5; width: 150px; }
  }
  
  /* Media query para mover o botão 5px para a direita apenas no modo mobile */
  @media (max-width: 768px) {
    div {
      transform: translateX(10px);
    }
  }
`;

// Botão de telefone
export const PhoneButton = styled.button`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(64, 128, 255, 0.15);
  border: 2px solid #4080ff;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 0 20px rgba(64, 128, 255, 0.5);
  position: relative;
  overflow: hidden;
  animation: pulseButton 2s infinite alternate;
  
  /* Espelhamento horizontal do ícone do telefone */
  svg {
    transform: scaleX(-1);
  }
  
  /* Efeito de círculos concêntricos */
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: transparent;
    pointer-events: none;
  }
  
  &::before {
    width: 110%;
    height: 110%;
    border: 2px dashed rgba(64, 128, 255, 0.4);
    animation: rotate 10s linear infinite;
  }
  
  &::after {
    width: 130%;
    height: 130%;
    border: 1px solid rgba(0, 64, 255, 0.2);
    animation: rotate 15s linear infinite reverse;
  }
  
  /* Efeito de brilho interno */
  svg {
    position: relative;
    z-index: 2;
    filter: drop-shadow(0 0 8px rgba(64, 128, 255, 0.8));
    transition: all 0.3s ease;
  }
  
  &:hover {
    background-color: rgba(64, 128, 255, 0.25);
    transform: scale(1.12) translateY(-5px);
    box-shadow: 
      0 0 30px rgba(64, 128, 255, 0.7),
      0 0 60px rgba(64, 128, 255, 0.3);
    border-color: #00ffff;
    
    svg {
      transform: scale(1.2);
      filter: drop-shadow(0 0 12px rgba(0, 64, 255, 0.8));
    }
  }
  
  @keyframes pulseButton {
    0% {
      box-shadow: 0 0 20px rgba(64, 128, 255, 0.5);
    }
    100% {
      box-shadow: 0 0 30px rgba(64, 128, 255, 0.8), 0 0 50px rgba(0, 64, 255, 0.3);
    }
  }
  
  @keyframes rotate {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

// Texto do botão
export const ButtonText = styled.div`
  margin-top: 1rem;
  font-size: 1.1rem;
  color: #64B6FF;
  font-weight: 600;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.5);
  position: relative;
  transition: all 0.3s ease;
  
  /* Efeito de destaque */
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: linear-gradient(to right, rgba(64, 128, 255, 0), rgba(0, 64, 255, 1), rgba(64, 128, 255, 0));
    transition: width 0.3s ease;
    opacity: 0;
  }
  
  /* Media query para ajustar o texto no modo mobile - APENAS NA PRIMEIRA DOBRA */
  ${PhoneButtonContainer} & {
    @media (max-width: 768px) {
      position: relative;
      left: -16px; /* Ajustado para 3px a mais à esquerda */
    }
  }
  
  ${PhoneButton}:hover + & {
    color: #00ffff;
    transform: translateY(-2px);
    text-shadow: 0 0 15px rgba(0, 64, 255, 0.7);
    
    &::after {
      width: 100%;
      opacity: 1;
    }
  }
`;

// Botão primário
export const PrimaryButton = styled.button`
  background: linear-gradient(to right, #4080ff, #34cfeb);
  color: white;
  border: none;
  padding: 0.9rem 1.8rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  
  &:hover {
    box-shadow: 0 5px 15px rgba(64, 128, 255, 0.4);
    transform: translateY(-2px);
    
    &::before {
      transform: translateX(0);
    }
  }
`;

// Modal
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(10, 25, 47, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  
  &.closing {
    animation: fadeOut 0.5s ease-out forwards;
  }
  
  /* Efeito de grade digital no fundo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      linear-gradient(rgba(64, 128, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(64, 128, 255, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    opacity: 0.3;
    pointer-events: none;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

export const ModalContent = styled.div`
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%);
  padding: 3rem;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.7), 
    0 0 40px rgba(64, 128, 255, 0.4),
    0 0 100px rgba(0, 64, 128, 0.15);
  position: relative;
  border: 1px solid rgba(64, 128, 255, 0.3);
  animation: modalFadeIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  overflow: hidden;
  
  &.closing {
    animation: modalFadeOut 0.5s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards;
  }
  
  /* Efeito de borda holográfica avançada */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      rgba(64, 128, 255, 0) 0%, 
      rgba(64, 128, 255, 0.6) 25%, 
      rgba(0, 64, 255, 0.6) 50%, 
      rgba(64, 128, 255, 0.6) 75%, 
      rgba(64, 128, 255, 0) 100%);
    background-size: 400% 400%;
    z-index: -1;
    border-radius: 18px;
    animation: holoGlow 8s linear infinite;
    filter: blur(3px);
    opacity: 0.7;
  }
  
  /* Efeito de matriz digital */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h20v20H0z' fill='none'/%3E%3Cpath d='M1 1h1v1H1zM5 1h1v1H5zM9 1h1v1H9zM13 1h1v1h-1zM17 1h1v1h-1zM3 3h1v1H3zM7 3h1v1H7zM11 3h1v1h-1zM15 3h1v1h-1zM1 5h1v1H1zM5 5h1v1H5zM9 5h1v1H9zM13 5h1v1h-1zM17 5h1v1h-1zM3 7h1v1H3zM7 7h1v1H7zM11 7h1v1h-1zM15 7h1v1h-1zM1 9h1v1H1zM5 9h1v1H5zM9 9h1v1H9zM13 9h1v1h-1zM17 9h1v1h-1zM3 11h1v1H3zM7 11h1v1H7zM11 11h1v1h-1zM15 11h1v1h-1zM1 13h1v1H1zM5 13h1v1H5zM9 13h1v1H9zM13 13h1v1h-1zM17 13h1v1h-1zM3 15h1v1H3zM7 15h1v1H7zM11 15h1v1h-1zM15 15h1v1h-1zM1 17h1v1H1zM5 17h1v1H5zM9 17h1v1H9zM13 17h1v1h-1zM17 17h1v1h-1z' fill='rgba(64, 128, 255, 0.1)'/%3E%3C/svg%3E");
    opacity: 0.15;
    z-index: -1;
    animation: matrixFlow 20s linear infinite;
  }
  
  /* Efeito de cantos tecnológicos */
  .tech-corner {
    position: absolute;
    width: 60px;
    height: 60px;
    pointer-events: none;
    z-index: 2;
  }
  
  .tech-corner-tl {
    top: 0;
    left: 0;
    border-top: 2px solid rgba(64, 128, 255, 0.8);
    border-left: 2px solid rgba(64, 128, 255, 0.8);
    border-top-left-radius: 8px;
    animation: cornerPulseTL 3s infinite alternate;
  }
  
  .tech-corner-tr {
    top: 0;
    right: 0;
    border-top: 2px solid rgba(0, 64, 255, 0.8);
    border-right: 2px solid rgba(0, 64, 255, 0.8);
    border-top-right-radius: 8px;
    animation: cornerPulseTR 3s infinite alternate;
  }
  
  .tech-corner-bl {
    bottom: 0;
    left: 0;
    border-bottom: 2px solid rgba(0, 64, 255, 0.8);
    border-left: 2px solid rgba(0, 64, 255, 0.8);
    border-bottom-left-radius: 8px;
    animation: cornerPulseBL 3s infinite alternate;
  }
  
  .tech-corner-br {
    bottom: 0;
    right: 0;
    border-bottom: 2px solid rgba(64, 128, 255, 0.8);
    border-right: 2px solid rgba(64, 128, 255, 0.8);
    border-bottom-right-radius: 8px;
    animation: cornerPulseBR 3s infinite alternate;
  }
  
  /* Efeito de linhas de código */
  .code-line {
    position: absolute;
    font-family: 'Courier New', monospace;
    font-size: 10px;
    color: rgba(64, 128, 255, 0.3);
    white-space: nowrap;
    pointer-events: none;
    z-index: 1;
    opacity: 0;
    animation: codeFade 8s infinite;
  }
  
  .code-line-1 {
    top: 20px;
    left: 20px;
    animation-delay: 0s;
  }
  
  .code-line-2 {
    bottom: 20px;
    right: 20px;
    animation-delay: 2s;
  }
  
  .code-line-3 {
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    animation-delay: 4s;
  }
  
  .code-line-4 {
    bottom: 50%;
    left: 20px;
    transform: translateY(50%);
    animation-delay: 6s;
  }
  
  /* Efeito de partículas tecnológicas */
  .tech-particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(64, 128, 255, 0.6);
    border-radius: 50%;
    pointer-events: none;
    z-index: 1;
    filter: blur(1px);
  }
  
  .particle-1 {
    top: 20%;
    left: 10%;
    animation: particleFloat 10s infinite;
  }
  
  .particle-2 {
    top: 70%;
    left: 80%;
    animation: particleFloat 12s infinite 1s;
    background: rgba(0, 64, 255, 0.6);
  }
  
  .particle-3 {
    top: 40%;
    left: 90%;
    animation: particleFloat 9s infinite 2s;
  }
  
  .particle-4 {
    top: 80%;
    left: 30%;
    animation: particleFloat 11s infinite 3s;
    background: rgba(0, 64, 255, 0.6);
  }
  
  /* Efeito de grade hexagonal */
  .hex-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='56' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 0l28 50-28 50L0 50 28 0z' fill='none' stroke='rgba(64, 128, 255, 0.1)' stroke-width='1'/%3E%3C/svg%3E");
    background-size: 56px 100px;
    opacity: 0.1;
    z-index: -1;
    animation: hexGridPulse 8s infinite alternate;
  }
  
  /* Efeito de scanner holográfico */
  .holo-scanner {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      rgba(64, 128, 255, 0) 0%,
      rgba(64, 128, 255, 0.05) 40%,
      rgba(0, 64, 255, 0.1) 50%,
      rgba(64, 128, 255, 0.05) 60%,
      rgba(64, 128, 255, 0) 100%);
    z-index: 3;
    pointer-events: none;
    animation: holoScan 8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
  
  /* Efeito de circuitos digitais */
  .circuit-lines {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      linear-gradient(90deg, transparent 98%, rgba(0, 64, 255, 0.2) 98%, transparent 100%),
      linear-gradient(0deg, transparent 98%, rgba(64, 128, 255, 0.2) 98%, transparent 100%),
      radial-gradient(circle at 50% 50%, rgba(64, 128, 255, 0.1) 0%, transparent 10%);
    background-size: 30px 30px, 30px 30px, 60px 60px;
    z-index: -1;
    opacity: 0.2;
    animation: circuitPulse 6s infinite alternate;
  }
  
  /* Efeito de dados binários */
  .binary-data {
    position: absolute;
    font-family: 'Courier New', monospace;
    font-size: 8px;
    color: rgba(64, 128, 255, 0.2);
    pointer-events: none;
    z-index: 1;
    opacity: 0.15;
    animation: binaryFade 5s infinite alternate;
  }
  
  .binary-1 {
    top: 10%;
    left: 5%;
    transform: rotate(-15deg);
  }
  
  .binary-2 {
    bottom: 15%;
    right: 8%;
    transform: rotate(15deg);
  }
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes modalFadeOut {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
  }
  
  @keyframes holoGlow {
    0% { background-position: 0% 50%; opacity: 0.5; }
    50% { opacity: 0.8; }
    100% { background-position: 400% 50%; opacity: 0.5; }
  }
  
  @keyframes matrixFlow {
    0% { background-position: 0 0; opacity: 0.1; }
    50% { opacity: 0.2; }
    100% { background-position: 100px 100px; opacity: 0.1; }
  }
  
  @keyframes cornerPulseTL {
    0% { width: 50px; height: 50px; border-width: 2px; }
    100% { width: 65px; height: 65px; border-width: 3px; }
  }
  
  @keyframes cornerPulseTR {
    0% { width: 50px; height: 50px; border-width: 2px; }
    100% { width: 65px; height: 65px; border-width: 3px; }
  }
  
  @keyframes cornerPulseBL {
    0% { width: 50px; height: 50px; border-width: 2px; }
    100% { width: 65px; height: 65px; border-width: 3px; }
  }
  
  @keyframes cornerPulseBR {
    0% { width: 50px; height: 50px; border-width: 2px; }
    100% { width: 65px; height: 65px; border-width: 3px; }
  }
  
  @keyframes codeFade {
    0% { opacity: 0; transform: translateX(-10px); }
    10% { opacity: 0.3; transform: translateX(0); }
    30% { opacity: 0.3; }
    40% { opacity: 0; transform: translateX(10px); }
    100% { opacity: 0; }
  }
  
  @keyframes particleFloat {
    0% { transform: translate(0, 0); opacity: 0.3; }
    25% { transform: translate(10px, 10px); opacity: 0.7; }
    50% { transform: translate(0, 20px); opacity: 0.3; }
    75% { transform: translate(-10px, 10px); opacity: 0.7; }
    100% { transform: translate(0, 0); opacity: 0.3; }
  }
  
  @keyframes hexGridPulse {
    0% { opacity: 0.05; background-size: 56px 100px; }
    100% { opacity: 0.15; background-size: 60px 106px; }
  }
  
  @keyframes holoScan {
    0% { left: -100%; opacity: 0.5; }
    48% { left: 100%; opacity: 0.8; }
    52% { left: 100%; opacity: 0; }
    52.001% { left: -100%; opacity: 0; }
    52.002% { left: -100%; opacity: 0.5; }
    100% { left: 100%; opacity: 0.8; }
  }
  
  @keyframes circuitPulse {
    0% { opacity: 0.1; background-position: 0 0; }
    100% { opacity: 0.3; background-position: 10px 10px; }
  }
  
  @keyframes binaryFade {
    0% { opacity: 0.1; }
    100% { opacity: 0.2; }
  }
`;

export const ModalTitle = styled.h2`
  color: white;
  margin-bottom: 2rem;
  font-size: 2.2rem;
  text-align: center;
  position: relative;
  padding-bottom: 18px;
  letter-spacing: 1px;
  text-shadow: 0 0 15px rgba(10, 59, 128, 0.4);
  animation: fadeIn 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(to right, #666666, #999999, #666666);
    border-radius: 3px;
    box-shadow: 0 0 10px rgba(150, 150, 150, 0.5);
    animation: widthPulse 3s ease-in-out infinite;
  }
  
  @keyframes widthPulse {
    0% { width: 80px; opacity: 0.8; }
    50% { width: 120px; opacity: 1; }
    100% { width: 80px; opacity: 0.8; }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(10, 59, 128, 0.15);
  border: 1px solid rgba(10, 59, 128, 0.3);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.7rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 10;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: currentColor;
    transition: all 0.3s ease;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
  
  &:hover {
    color: #ffffff;
    background-color: rgba(10, 59, 128, 0.3);
    transform: rotate(90deg) scale(1.1);
    box-shadow: 0 0 15px rgba(10, 59, 128, 0.4);
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  z-index: 2;
  
  /* Efeito de linhas de conexão */
  &::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, 
      rgba(64, 128, 255, 0) 0%, 
      rgba(64, 128, 255, 0.2) 20%, 
      rgba(64, 128, 255, 0.2) 80%, 
      rgba(64, 128, 255, 0) 100%);
    z-index: -1;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  position: relative;
  
  /* Efeito de conexão horizontal */
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 50%;
    width: 15px;
    height: 2px;
    background-color: rgba(64, 128, 255, 0.2);
    transform: translateY(-50%);
  }
  
  /* Efeito de ponto de conexão */
  &::after {
    content: '';
    position: absolute;
    left: -25px;
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: rgba(64, 128, 255, 0.4);
    transform: translateY(-50%);
  }
  
  /* Animação sequencial para cada grupo */
  animation: slideIn 0.5s ease-out both;
  animation-delay: calc(0.1s * var(--index, 0));
  
  &:nth-child(1) { --index: 1; }
  &:nth-child(2) { --index: 2; }
  &:nth-child(3) { --index: 3; }
  &:nth-child(4) { --index: 4; }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

export const Label = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: #64B6FF;
    border-radius: 50%;
    margin-right: 10px;
    box-shadow: 0 0 8px rgba(64, 128, 255, 0.6);
    transition: all 0.3s ease;
  }
  
  /* Efeito hover ao focar no input associado */
  input:focus + & {
    color: #00ffff;
    transform: translateX(5px);
    
    &::before {
      background-color: #00ffff;
      box-shadow: 0 0 12px rgba(0, 64, 255, 0.8);
    }
  }
`;

export const Input = styled.input`
  padding: 1rem 1.2rem;
  border-radius: 8px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  background-color: rgba(16, 32, 64, 0.4);
  color: white;
  transition: all 0.3s ease;
  font-size: 1rem;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 2;
  order: 2; /* Coloca o input acima do label visualmente */
  
  /* Efeito de brilho interno */
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: none;
    border-color: #00ffff;
    box-shadow: 
      inset 0 0 10px rgba(0, 0, 0, 0.3),
      0 0 0 2px rgba(0, 64, 255, 0.2),
      0 0 15px rgba(0, 64, 255, 0.1);
    background-color: rgba(16, 32, 64, 0.6);
    transform: translateY(-2px);
  }
  
  /* Efeito de digitação */
  &:not(:placeholder-shown) {
    border-color: rgba(64, 128, 255, 0.6);
    background-color: rgba(16, 32, 64, 0.5);
  }
`;

export const SubmitButton = styled(PrimaryButton)`
  margin-top: 2rem;
  width: 100%;
  font-size: 1.1rem;
  letter-spacing: 1px;
  height: 54px;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  background: linear-gradient(45deg, #0a3b80, #1a4d95, #0d2b5e, #1a4d95);
  background-size: 300% 100%;
  animation: gradientShift 5s ease infinite;
  transition: all 0.4s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(10, 59, 128, 0.3);
  
  /* Efeito de brilho ao passar o mouse */
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(64, 128, 255, 0.2) 0%, rgba(255, 255, 255, 0) 70%);
    opacity: 0;
    transition: opacity 0.5s ease;
    mix-blend-mode: overlay;
    pointer-events: none;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 0 30px rgba(10, 59, 128, 0.4);
    
    &::after {
      opacity: 1;
    }
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: linear-gradient(45deg, #0a3b80, #1a4d95);
  }
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

export const StatusMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1.2rem;
  background-color: ${props => props.success ? 'rgba(0, 200, 83, 0.15)' : 'rgba(231, 76, 60, 0.15)'};
  border: 1px solid ${props => props.success ? 'rgba(0, 200, 83, 0.3)' : 'rgba(231, 76, 60, 0.3)'};
  border-radius: 8px;
  color: ${props => props.success ? '#00ffaa' : '#ff5252'};
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 20px ${props => props.success ? 'rgba(0, 255, 170, 0.2)' : 'rgba(255, 82, 82, 0.2)'};
  animation: messageSlideIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  /* Efeito de brilho pulsante */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      ${props => props.success ? 'rgba(0, 255, 170, 0.3)' : 'rgba(255, 82, 82, 0.3)'} 0%,
      transparent 70%
    );
    opacity: 0;
    animation: pulseBrightness 2s infinite;
  }
  
  /* Ícone de status */
  &::after {
    content: '${props => props.success ? '✓' : '✕'}';
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.2rem;
    opacity: 0.8;
  }
  
  @keyframes messageSlideIn {
    from { opacity: 0; transform: translateY(-15px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  
  @keyframes pulseBrightness {
    0% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 0; }
  }
`;
